import type { FactoryStageSummary } from '../api.ts';

/**
 * A factory drawn as its stages, left to right.
 *
 * Horizontal here where the workflow graph is vertical, and for the opposite
 * reason: the graph is read for order within one document, this is read for
 * *where the work is* across several. Six boxes in a row with a count under
 * each is a board; a column would be a list.
 *
 * A stage this deployment has not got is drawn dashed rather than left out.
 * "Release is not deployed here" is the fact somebody needs, and a strip with
 * five boxes looks like a five-stage factory.
 */
export function StageStrip({
  stages,
  selected,
  closesLoop,
  onSelect,
  compact = false,
}: {
  stages: readonly FactoryStageSummary[];
  selected?: string | null;
  closesLoop?: boolean;
  onSelect?(stageId: string): void;
  compact?: boolean;
}) {
  /*
   * The wrapper is a size container so the strip can wrap to a second row
   * when the column it sits in is narrow, without a ResizeObserver. A
   * `@container` rule applies to a container's descendants and never to the
   * container itself, so the query has to live one level up from the `ol`
   * whose `flex-wrap` it toggles. Below ~700px six boxes on one line had
   * ellipsized their names to a letter each, which is a strip that says
   * nothing.
   */
  return (
    <div className="stages-wrap">
      <ol className={`stages ${compact ? 'compact' : ''}`}>
        {stages.map((stage, index) => {
          const tone = stage.waiting > 0 ? 'warn' : stage.running > 0 ? 'active' : '';
          const Element = onSelect ? 'button' : 'div';
          return (
            <li key={stage.id} className={stage.present ? '' : 'absent'}>
              <Element
                type={onSelect ? 'button' : undefined}
                className={`stage ${tone}`}
                aria-current={selected === stage.id ? 'true' : undefined}
                onClick={onSelect ? () => onSelect(stage.id) : undefined}
              >
                <span className="stage-index">{index + 1}</span>
                <span className="stage-name">{stage.name}</span>
                {stage.gate && (
                  <span className="stage-gate" title={`A person decides at ${stage.gate}`}>
                    gate
                  </span>
                )}
                {!compact && (
                  <span className="stage-counts">
                    {stage.items > 0 && <span>{stage.items} item{stage.items === 1 ? '' : 's'}</span>}
                    {stage.running > 0 && (
                      <span className="active">
                        {stage.running} running{stage.lastRunAt ? ` · ${age(stage.lastRunAt)}` : ''}
                      </span>
                    )}
                    {stage.waiting > 0 && <span className="warn">{stage.waiting} waiting</span>}
                    {stage.items === 0 && stage.running === 0 && stage.waiting === 0 && (
                      <span className="faint">{stage.present ? 'idle' : 'not deployed'}</span>
                    )}
                  </span>
                )}
              </Element>
              {index < stages.length - 1 && <span className="stage-arrow" aria-hidden="true" />}
            </li>
          );
        })}
        {closesLoop && (
          <li className="stage-loop" aria-label="The last stage feeds the first">
            ↺
          </li>
        )}
      </ol>
    </div>
  );
}

/**
 * How long a stage has been running, in the fewest characters that say it.
 *
 * "2 running" alone cannot tell a stage that started a minute ago from one
 * that has been stuck since yesterday, and the second is the one worth a
 * click. No "ago" suffix: the box is narrow, and beside "running" the word
 * would say the run *ended*.
 */
function age(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86_400)}d`;
}
